import {
  Box,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  SxProps,
} from "@mui/material";
import React from "react";
import { ReactElement, useCallback, useEffect, useState } from "react";
import { NavLink } from "react-router-dom";

import Back from "~frontend/components/icons/Back";
import theme, { palette } from "~frontend/theme";

import { FlexBox } from "./FlexBox";
import SupportWidget from "./SupportWidget";

export type SidebarItem = {
  label: string;
  path: string;
};

export type PrimarySidebarItem = SidebarItem & {
  icon: React.ReactNode;
  activeIcon: React.ReactNode;
};

export const collapsedWidth = "60px";
export const expandedWidth = "238px";

const LIST_ITEM_HEIGHTS = {
  default: {
    height: 22,
    secondaryHeight: 18,
    paddingY: 12,
  },
  small: {
    height: 18,
    secondaryHeight: 18,
    paddingY: 5,
  },
};

const outerWrapperBaseStyle: SxProps = {
  minHeight: "100vh",
  zIndex: 1200,
};

const outerWrapperCollapsedStyle: SxProps = {
  ...outerWrapperBaseStyle,
  width: collapsedWidth,
  overflowY: "visible",
};

const outerWrapperExpandedStyle: SxProps = {
  ...outerWrapperBaseStyle,
  width: expandedWidth,
};

const listItemStyle: SxProps = {
  "& .MuiSvgIcon-root": {
    marginRight: "10px",
  },
};

const listStyle: SxProps = {
  padding: 0,
  width: expandedWidth,
  flex: 1,
  "& svg": {
    fontSize: "18px",
  },
  "& .activeIcon": {
    display: "none",
  },
  "& .active": {
    "& .activeIcon": {
      display: "flex",
    },
    "& .inactiveIcon": {
      display: "none",
    },
  },
};

const MIN_WIDTH_TO_COLLAPSE = 1200;

export function useCollapsedState(): [
  boolean,
  null | ((newState: boolean) => void),
] {
  const [isTooSmallToExpand, setIsTooSmallToExpand] = useState(
    window.innerWidth < MIN_WIDTH_TO_COLLAPSE
  );
  const [manualCollapse, setManualCollapse] = useState(
    localStorage.getItem("sidebarCollapsed") === "collapsed"
  );

  const setCollapsed = useCallback((newState: boolean) => {
    localStorage.setItem(
      "sidebarCollapsed",
      newState ? "collapsed" : "expanded"
    );
    setManualCollapse(newState);
  }, []);

  useEffect(() => {
    function resizeHandler() {
      setIsTooSmallToExpand(window.innerWidth < MIN_WIDTH_TO_COLLAPSE);
    }

    window.addEventListener("resize", resizeHandler);
    return () => window.removeEventListener("resize", resizeHandler);
  }, []);

  return [
    isTooSmallToExpand || manualCollapse,
    isTooSmallToExpand ? null : setCollapsed,
  ];
}

export default function Sidebar({
  primaryItems,
  secondaryItems,
  collapsed,
  setCollapsed,
}: {
  primaryItems: PrimarySidebarItem[];
  secondaryItems: SidebarItem[];
  collapsed: boolean;
  setCollapsed: ((newState: boolean) => void) | null;
}): ReactElement {
  const heightOfAllButtonsPlusBuffer =
    (primaryItems.length + 1) *
      (LIST_ITEM_HEIGHTS.default.height +
        LIST_ITEM_HEIGHTS.default.paddingY * 2) +
    secondaryItems.length * LIST_ITEM_HEIGHTS.default.secondaryHeight +
    20;
  const heightOfAllButtonsSmallPlusBuffer =
    (primaryItems.length + 1) *
      (LIST_ITEM_HEIGHTS.small.height + LIST_ITEM_HEIGHTS.small.paddingY * 2) +
    secondaryItems.length * LIST_ITEM_HEIGHTS.small.secondaryHeight +
    50;

  const innerWrapperBaseStyle: SxProps = {
    // 1. these are default menu styles when the window height is greater than the
    // combined height of all menu items
    height: "100vh",
    position: "sticky",
    top: 0,
    left: 0,
    background: palette.lightGray,
    zIndex: 1200,
    display: "flex",
    flexDirection: "column",

    "& .listItem": {
      padding: `${LIST_ITEM_HEIGHTS.default.paddingY}px 24px`,
    },

    // 3. if the window height is less than the combined height of the shrunk menu items,
    // remove position: sticky so that the menu scrolls with the page
    [`@media (max-height: ${heightOfAllButtonsSmallPlusBuffer}px)`]: {
      height: "100%",
      position: "relative",
    },

    // 2. if the window height is less than the combined height of all menu items,
    // shrink the text slightly and reduce vertical padding
    [`@media (max-height: ${heightOfAllButtonsPlusBuffer}px)`]: {
      "& .listItem": {
        padding: `${LIST_ITEM_HEIGHTS.small.paddingY}px 24px`,
        fontSize: "12px",
      },
    },
  };

  const innerWrapperExpandedStyle: SxProps = {
    ...innerWrapperBaseStyle,
    width: expandedWidth,
  };

  const innerWrapperCollapsedStyle: SxProps = {
    ...innerWrapperBaseStyle,
    width: collapsedWidth,
    overflowY: "hidden",
    transition: "width 0.25s",

    "& .listItemText": {
      opacity: 0,
    },

    "& .listItem": {
      padding: `${LIST_ITEM_HEIGHTS.default.paddingY}px 20px`,

      [`@media (max-height: ${heightOfAllButtonsPlusBuffer}px)`]: {
        padding: `${LIST_ITEM_HEIGHTS.small.paddingY}px 20px !important`,
      },
    },

    "& .expandButtonIcon, & .secondaryItems": {
      display: "none",
    },

    "&:hover:not(.suppressHover)": {
      width: expandedWidth,
      "& .menuButton": {
        padding: "12px 24px",
      },

      "& .expandButtonIcon, & .secondaryItems": {
        display: "inline-block",
      },

      "& .listItemText": {
        opacity: 1,
      },
    },
  };

  // we suppress the :hover pseudo-class styles right after you click so that
  // when you navigate to page, we shrink the sidebar
  const [suppressHover, setSuppressHover] = useState(false);

  return (
    <Box
      sx={collapsed ? outerWrapperCollapsedStyle : outerWrapperExpandedStyle}
    >
      <Box
        sx={collapsed ? innerWrapperCollapsedStyle : innerWrapperExpandedStyle}
        className={suppressHover ? "suppressHover" : ""}
      >
        <List sx={listStyle}>
          <ListItemButton
            className="menuButton"
            onClick={() => {
              setSuppressHover(true);
              setCollapsed?.(!collapsed);
            }}
            onMouseLeave={() => {
              setSuppressHover(false);
            }}
            dense
            disabled={!setCollapsed}
            sx={{
              ...theme.typography.h3Caps,
              color: palette.darkGray,
              padding: collapsed ? "12px" : "12px 24px",

              "&.Mui-disabled": {
                opacity: 1,
              },
            }}
          >
            <ListItemText
              disableTypography
              primary={
                <>
                  {setCollapsed && !collapsed && (
                    <Back
                      sx={{
                        width: "10px",
                        height: "10px",
                        marginRight: "4px",
                        marginBottom: "-1px",
                      }}
                    />
                  )}
                  Menu
                  {setCollapsed && collapsed && (
                    <Back
                      className="expandButtonIcon"
                      sx={{
                        width: "10px",
                        height: "10px",
                        marginLeft: "4px",
                        marginBottom: "-1px",
                        transform: "rotate(180deg)",
                      }}
                    />
                  )}
                </>
              }
            />
          </ListItemButton>

          {primaryItems.map(({ label, path, activeIcon, icon }, idx) => (
            <ListItemButton
              dense
              key={idx}
              onClick={() => {
                setSuppressHover(true);
              }}
              onMouseLeave={() => {
                setSuppressHover(false);
              }}
              component={React.forwardRef((props: any, ref) => (
                <NavLink
                  ref={ref}
                  {...props}
                  style={({ isActive }) => ({
                    fontWeight: isActive ? "bold" : "normal",
                  })}
                />
              ))}
              to={path}
              className="listItem"
              sx={{
                ...theme.typography.body1,
                ...listItemStyle,
              }}
            >
              <FlexBox className="activeIcon">{activeIcon}</FlexBox>
              <FlexBox className="inactiveIcon">{icon}</FlexBox>

              <ListItemText
                className="listItemText"
                disableTypography
                sx={{ lineHeight: 1 }}
                primary={label}
              />
            </ListItemButton>
          ))}
          {secondaryItems.length > 0 && <Divider />}
          <Box className="secondaryItems" sx={{ width: expandedWidth }}>
            {secondaryItems.map(({ label, path }, idx) => (
              <ListItemButton
                dense
                key={idx}
                onClick={() => {
                  setSuppressHover(true);
                }}
                onMouseLeave={() => {
                  setSuppressHover(false);
                }}
                component={React.forwardRef((props: any, ref) => (
                  <NavLink
                    ref={ref}
                    {...props}
                    style={({ isActive }) => ({
                      fontWeight: isActive ? "bold" : "normal",
                    })}
                  />
                ))}
                to={path}
                sx={{
                  ...theme.typography.body2,
                  ...listItemStyle,
                }}
              >
                <ListItemText disableTypography primary={label} />
              </ListItemButton>
            ))}
          </Box>
        </List>
        <SupportWidget />
      </Box>
    </Box>
  );
}
